// Site-wide progressive enhancements (vanilla JS, no dependencies):
//   - theme toggle persisted in localStorage (the inline script in the
//     layout applies it before first paint; this file owns the button)
//   - aria-current on the main nav
//   - spec page: highlight the TOC entry of the section in view
//   - data-confirm on forms (delete buttons)

const THEME_KEY = 'askdown-theme';

function readTheme() {
  try {
    const t = localStorage.getItem(THEME_KEY);
    return t === 'light' || t === 'dark' ? t : null;
  } catch {
    return null;
  }
}

function writeTheme(theme) {
  try {
    if (theme) localStorage.setItem(THEME_KEY, theme);
    else localStorage.removeItem(THEME_KEY);
  } catch {
    /* private mode, blocked storage: the attribute still applies for this page */
  }
}

/** The theme in effect: the override, else the OS preference. */
export function effectiveTheme() {
  return (
    document.documentElement.getAttribute('data-theme') ||
    (matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
  );
}

export function setTheme(theme) {
  document.documentElement.setAttribute('data-theme', theme);
  writeTheme(theme);
  document.dispatchEvent(new CustomEvent('askdown:theme', { detail: { theme } }));
}

function initThemeToggle() {
  for (const button of document.querySelectorAll('[data-theme-toggle]')) {
    button.addEventListener('click', () => {
      setTheme(effectiveTheme() === 'dark' ? 'light' : 'dark');
    });
  }
  // Follow OS changes only while no explicit choice is stored.
  matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (!readTheme()) document.documentElement.removeAttribute('data-theme');
  });
}

function initNavCurrent() {
  const path = location.pathname;
  for (const a of document.querySelectorAll('.site-nav a')) {
    const href = a.getAttribute('href');
    if (!href) continue;
    const current = href === '/' ? path === '/' : path === href || path.startsWith(href + '/');
    if (current) a.setAttribute('aria-current', 'page');
  }
}

function initSpecToc() {
  const toc = document.querySelector('.spec-toc');
  const article = document.querySelector('.spec-content');
  if (!toc || !article || !('IntersectionObserver' in window)) return;
  const links = new Map();
  for (const a of toc.querySelectorAll('a[href^="#"]')) links.set(a.getAttribute('href').slice(1), a);
  const headings = [...article.querySelectorAll('h2[id], h3[id]')].filter((h) => links.has(h.id));
  if (!headings.length) return;

  let activeId = null;
  const setActive = (id) => {
    if (id === activeId) return;
    activeId = id;
    for (const [hid, a] of links) {
      if (hid === id) {
        a.setAttribute('aria-current', 'true');
        // Keep the active entry visible inside the scrolling TOC.
        const box = toc.getBoundingClientRect();
        const r = a.getBoundingClientRect();
        if (r.top < box.top || r.bottom > box.bottom) a.scrollIntoView({ block: 'nearest' });
      } else a.removeAttribute('aria-current');
    }
  };

  // Pick the last heading above the reading line.
  const update = () => {
    const line = 96;
    let current = headings[0];
    for (const h of headings) {
      if (h.getBoundingClientRect().top <= line) current = h;
      else break;
    }
    setActive(current.id);
  };
  let ticking = false;
  addEventListener(
    'scroll',
    () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        update();
      });
    },
    { passive: true },
  );
  update();

  // On small screens the TOC is a <details>; collapse it after choosing.
  toc.addEventListener('click', (e) => {
    if (e.target instanceof HTMLAnchorElement && matchMedia('(max-width: 860px)').matches) toc.open = false;
  });
  if (matchMedia('(max-width: 860px)').matches) toc.open = false;
}

function initConfirms() {
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (form instanceof HTMLFormElement && form.dataset.confirm && !confirm(form.dataset.confirm)) {
      e.preventDefault();
    }
  });
}

initThemeToggle();
initNavCurrent();
initSpecToc();
initConfirms();
